import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCookieConsent } from '../contexts/CookieConsentContext';

interface CookiePolicyProps {
  onNavigate: (page: string) => void;
}

const CookiePolicy: React.FC<CookiePolicyProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const { resetConsent } = useCookieConsent();

  const handleUpdatePreferences = () => {
    resetConsent();
    onNavigate('home');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-sm rounded-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('cookiePolicy.title')}</h1>
          
          <div className="prose max-w-none">
            <p className="text-lg text-gray-600 mb-6">
              {t('cookiePolicy.lastUpdated', { date: new Date().toLocaleDateString() })}
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('cookiePolicy.sections.whatAreCookies.title')}</h2>
              <p className="text-gray-700 mb-4">
                {t('cookiePolicy.sections.whatAreCookies.content')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('cookiePolicy.sections.howWeUseCookies.title')}</h2>
              <p className="text-gray-700 mb-4">
                {t('cookiePolicy.sections.howWeUseCookies.content')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('cookiePolicy.sections.typesOfCookies.title')}</h2>
              
              <div className="space-y-6">
                <div className="border-l-4 border-black-500 pl-4">
                  <h3 className="text-xl font-medium text-gray-900 mb-2">{t('cookiePolicy.sections.typesOfCookies.necessary.title')}</h3>
                  <p className="text-gray-700 mb-2">
                    {t('cookiePolicy.sections.typesOfCookies.necessary.description')}
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    {t('cookiePolicy.sections.typesOfCookies.necessary.list', { returnObjects: true }).map((item: string, index: number) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>

                <div className="border-l-4 border-black-500 pl-4">
                  <h3 className="text-xl font-medium text-gray-900 mb-2">{t('cookiePolicy.sections.typesOfCookies.analytics.title')}</h3>
                  <p className="text-gray-700 mb-2">
                    {t('cookiePolicy.sections.typesOfCookies.analytics.description')}
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    {t('cookiePolicy.sections.typesOfCookies.analytics.items', { returnObjects: true }).map((item: string, index: number) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                  <p className="text-sm text-gray-600 mt-3 italic">
                    {t('cookiePolicy.sections.typesOfCookies.analytics.note')}
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('cookiePolicy.sections.thirdPartyCookies.title')}</h2>
              <p className="text-gray-700 mb-4">
                {t('cookiePolicy.sections.thirdPartyCookies.description')}
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                {(t('cookiePolicy.sections.thirdPartyCookies.services', { returnObjects: true }) as any[]).map((service: any, index: number) => (
                  <li key={index}><strong>{service.name}:</strong> {service.description}</li>
                ))}
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('cookiePolicy.sections.managingCookies.title')}</h2>
              <p className="text-gray-700 mb-4">
                {t('cookiePolicy.sections.managingCookies.description')}
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                {(t('cookiePolicy.sections.managingCookies.methods', { returnObjects: true }) as string[]).map((method: string, index: number) => (
                  <li key={index}>{method}</li>
                ))}
              </ul>
              
              <div className="bg-gradient-to-r from-primary/5 to-secondary/5 border border-primary/20 rounded-xl p-6">
                <button
                  onClick={handleUpdatePreferences}
                  className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-full text-white bg-gradient-primary hover:shadow-button focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/50 transition-all duration-200"
                >
                  {t('cookiePolicy.sections.managingCookies.updatePreferences.button')}
                </button>
                <p className="text-sm text-gray-700 mt-3">
                  {t('cookiePolicy.sections.managingCookies.updatePreferences.description')}
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('cookiePolicy.sections.cookieRetention.title')}</h2>
              <p className="text-gray-700 mb-4">
                {t('cookiePolicy.sections.cookieRetention.description')}
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                {(t('cookiePolicy.sections.cookieRetention.types', { returnObjects: true }) as any[]).map((item: any, index: number) => (
                  <li key={index}><strong>{item.type}</strong> {item.retention}</li>
                ))}
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('cookiePolicy.sections.contactUs.title')}</h2>
              <p className="text-gray-700 mb-4">
                {t('cookiePolicy.sections.contactUs.description')}
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Email: contact@kexa.io</li>
                <li>Website: <button onClick={() => onNavigate('contact')} className="text-blue-600 hover:underline">Contact page</button></li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('cookiePolicy.sections.changesToPolicy.title')}</h2>
              <p className="text-gray-700">
                {t('cookiePolicy.sections.changesToPolicy.content')}
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;